import { Fragment } from "react"
import type { FC, ReactElement } from "react"

import eq from "lodash-es/eq"

import { ETypographyColor, Typography } from "@/3514/components"
import Avatar from "@/shared/avatar/Avatar"
import { richTextRendererUtils } from "@/shared/rich-text-renderer"

import { useCoachingChatContext as useChatContext } from "../context"
import { useCoachingChatConversationMessageByIdSelector as useMessageSelector } from "../store"
import { coachingChatStyles as chatStyles } from "../styles"
import {
    ECoachingChatEntityMode as EEntityMode,
    ECoachingChatMessageAuthor as EMessageAuthor,
    type TCoachingChatConversationMessage as TMessage
} from "../types"

import { coachingChatOwlComponents as owls } from "./coaching-chat.owls-components"

type TProps = {
    clientMessageId: string
    iconFillColor?: "blue-500" | "blue-600"
}

const { isStringPlainText } = richTextRendererUtils
const {
    conversation: { message: classes }
} = chatStyles

const avatarDimension: number = 30

const MessageLight: FC<TProps> = ({ clientMessageId, iconFillColor = "blue-600" }: TProps): ReactElement => {
    const {
        entityId,
        entityMode,
        conversation: { renderMessage, user }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const message: TMessage | undefined = useMessageSelector(entityId, clientMessageId)

    if (!message) return <Fragment />

    const { content: messageText, author }: TMessage = message

    return (
        <div className={classes.container({ author, isNotInteractive: true, isLight: true })}>
            <div className={classes.messageRow({ author })}>
                <div className={classes.messageWrapper()}>
                    <div className={classes.message({ author })}>
                        {isStringPlainText(messageText as string) ? (
                            <Typography
                                text={messageText}
                                color={
                                    eq(entityMode, EEntityMode.Full) ? ETypographyColor.Dark : ETypographyColor.White
                                }
                                isPretty
                                withHyphens
                            />
                        ) : (
                            renderMessage(messageText as string)
                        )}
                    </div>
                </div>
                {eq(author, EMessageAuthor.User) ? (
                    <Avatar
                        width={avatarDimension}
                        height={avatarDimension}
                        url={user?.photo}
                        alt={`${user?.firstName} avatar`}
                        imgClassName={`min-w-[${avatarDimension}px]`}
                    />
                ) : (
                    <owls.DefaultLogo fill={iconFillColor} />
                )}
            </div>
        </div>
    )
}

MessageLight.displayName = "CoachingChatMessageLight"

export { MessageLight as CoachingChatMessageLight }
